import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { api, type SuspiciousDataClient } from '../../lib/api';
import { unwrapApi } from '../../lib/unwrapApi';

interface FixIntervalModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: SuspiciousDataClient;
  currentInterval?: number | null;
  onFixed?: () => void;
}

const QUICK_INTERVALS = [7, 14, 21, 28];

export function FixIntervalModal({
  isOpen,
  onClose,
  client,
  currentInterval,
  onFixed,
}: FixIntervalModalProps) {
  const queryClient = useQueryClient();
  const [intervalDays, setIntervalDays] = useState('');

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      setIntervalDays(currentInterval?.toString() || '');
    }
  }, [isOpen, currentInterval]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const days = parseInt(intervalDays, 10);
      if (isNaN(days) || days <= 0) {
        throw new Error('Invalid interval');
      }
      return api.clients.update(client.id, { intervalDays: days }).then(unwrapApi);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onFixed?.();
      handleClose();
    },
  });

  const handleClose = () => {
    setIntervalDays('');
    onClose();
  };

  const handleQuickSelect = (days: number) => {
    setIntervalDays(String(days));
  };

  const parsedInterval = parseInt(intervalDays, 10);
  const isValid = !isNaN(parsedInterval) && parsedInterval > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div
        className="bg-white w-full sm:w-[420px] sm:rounded-2xl sm:max-h-[90vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-forest-800 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold">Fix Interval</h2>
              <p className="text-sm text-white/70 mt-0.5">{client.clientName}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Client Info */}
          <div className="bg-sage-50 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-sage-600">Route</p>
                <p className="font-medium text-forest-900 font-mono">{client.routeId || 'No route'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-sage-600">Current Interval</p>
                <p className="font-medium text-amber-600">
                  {currentInterval === null || currentInterval === undefined
                    ? 'Not set'
                    : currentInterval === 0
                    ? '0 (invalid)'
                    : `${currentInterval} days`}
                </p>
              </div>
            </div>
          </div>

          {/* Interval Input */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-2">
              Service Interval (days)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={intervalDays}
              onChange={(e) => setIntervalDays(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter number of days"
              className={`
                w-full px-4 py-3 rounded-xl border-2 text-lg font-mono text-center
                ${isValid
                  ? 'border-primary-500 bg-primary-50'
                  : intervalDays
                  ? 'border-red-300'
                  : 'border-sage-200'
                }
                focus:border-primary-500 focus:outline-none
              `}
            />
            {intervalDays && !isValid && (
              <p className="text-sm text-red-600 mt-1">Please enter a valid number greater than 0</p>
            )}
          </div>

          {/* Quick Select */}
          <div>
            <p className="text-sm text-sage-600 mb-2">Quick select:</p>
            <div className="flex gap-2">
              {QUICK_INTERVALS.map((days) => (
                <button
                  key={days}
                  onClick={() => handleQuickSelect(days)}
                  className={`
                    flex-1 py-2.5 px-3 rounded-xl border-2 font-medium transition-colors
                    ${parsedInterval === days
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-sage-200 text-sage-600 hover:border-sage-300'
                    }
                  `}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>

          {/* Helper text */}
          <p className="text-sm text-sage-500">
            Common intervals: 7 days (weekly), 14 days (bi-weekly), 21 days (every 3 weeks), 28 days (monthly)
          </p>

          {/* Error Display */}
          {saveMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-700">
                Failed to update interval. Please try again.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-sage-200 px-6 py-4 flex gap-3">
          <Button variant="ghost" onClick={handleClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => saveMutation.mutate()}
            disabled={!isValid || saveMutation.isPending}
            className="flex-1"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
